// HistoryPage — /studio/:slug/:repo/_/history/:ref
// Shows paginated commit history for the selected branch/ref.

import { useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useLog } from "../diff/useLog.js";
import { useRepoRefs } from "../diff/useRepoRefs.js";
import { BranchPicker } from "./BranchPicker.js";
import { CommitRow } from "./CommitRow.js";
import { Pager } from "./Pager.js";
import { Spinner } from "../ui/Spinner.js";
import { Banner } from "../ui/Banner.js";
import { PageHeader } from "../ui/PageHeader.js";
import { repoHistory, repoCommit } from "../lib/paths";
import { useTranslation } from "../i18n";

const PAGE_SIZE = 20;

export function HistoryPage() {
  const { t } = useTranslation("history");
  const { slug = "", repo = "", ref: refParam = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPage = Number(searchParams.get("page") ?? "1");
  const page = Number.isFinite(rawPage) ? Math.max(1, Math.floor(rawPage)) : 1;
  const navigate = useNavigate();

  // A repo's default branch is recorded before any commit exists, so a link
  // built from it can name a branch that was never created (the first push
  // landed on `arch/…`). Follow what the repo actually has: the server's
  // default if it is real, else the first branch. Without this the page asks
  // for a missing ref and the picker — a <select> whose value matches no
  // option — shows a different branch than the one being queried.
  const refs = useRepoRefs({ slug, repo });
  const known = [...(refs.data?.branches ?? []), ...(refs.data?.tags ?? [])];
  const serverDefault = refs.data?.defaultBranch;
  const fallbackRef =
    serverDefault && known.includes(serverDefault) ? serverDefault : known[0];
  const refExists = refParam !== "" && known.includes(refParam);
  const ref = refExists || known.length === 0 ? refParam : (fallbackRef ?? refParam);

  useEffect(() => {
    if (ref && ref !== refParam) navigate(repoHistory(slug, repo, ref), { replace: true });
  }, [ref, refParam, slug, repo, navigate]);

  const { data, error, isLoading } = useLog({
    slug,
    repo,
    ref,
    page,
    pageSize: PAGE_SIZE,
  });

  // The server tells us directly whether more pages exist.
  const hasMore = data?.hasMore ?? false;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <PageHeader
        title={t("page.title")}
        description={t("page.description")}
        actions={
          <BranchPicker
            slug={slug}
            repo={repo}
            value={ref}
            onChange={(next) => navigate(repoHistory(slug, repo, next))}
          />
        }
      />

      {isLoading && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {error && <Banner kind="error">{String(error)}</Banner>}

      {data && (
        <>
          <ul className="divide-y divide-slate-800 overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
            {data.commits.map((c) => (
              <CommitRow
                key={c.oid}
                commit={c}
                onClick={(oid) => navigate(repoCommit(slug, repo, oid))}
              />
            ))}
          </ul>
          <div className="mt-4">
            <Pager
              page={page}
              hasMore={hasMore}
              onPage={(p) => setSearchParams({ page: String(p) })}
            />
          </div>
        </>
      )}
    </div>
  );
}
